export type ConditionalLogicRule = {
  questionIndex: number;
  operator: string;
  value: string;
};

export type ConditionalLogic = {
  enabled?: boolean;
  matchMode?: 'all' | 'any';
  rules?: ConditionalLogicRule[];
} | null | undefined;

const toComparable = (answer: unknown): string => {
  if (answer == null) return '';
  if (Array.isArray(answer)) return answer.join(', ');
  return String(answer);
};

// why: contains on a multi-select answer must mean "is one of the options", never a substring
// why: match — else 'apple' matches ['pineapple'].
const matchesContains = (answer: unknown, value: string): boolean => {
  const needle = value.toLowerCase();
  if (Array.isArray(answer)) {
    return answer.some((el) => String(el ?? '').toLowerCase() === needle);
  }
  return toComparable(answer).toLowerCase().includes(needle);
};

const evalRule = (rule: ConditionalLogicRule, answer: unknown): boolean => {
  const left = toComparable(answer);
  const right = rule.value ?? '';
  switch (rule.operator) {
    // why: equals comparisons must trim both sides — untrimmed dropdown values can leave a gated
    // why: follow-up question permanently hidden.
    case 'equals': return left.trim() === right.trim();
    case 'not_equals': return left.trim() !== right.trim();
    case 'gte': return Number(left) >= Number(right);
    case 'lte': return Number(left) <= Number(right);
    case 'contains': return matchesContains(answer, right);
    case 'not_contains': return !matchesContains(answer, right);
    case 'empty': return left === '';
    case 'not_empty': return left !== '';
    default: return true;
  }
};

// gloss
type EvaluateConditionalLogicOptions = {
  onParseError?: (err: unknown, rawValue: string) => void;
};

// gloss
export const evaluateConditionalLogic = (
  conditionalLogic: ConditionalLogic | string,
  answersByQuestionNumber: Record<number, unknown>,
  options?: EvaluateConditionalLogicOptions,
): boolean => {
  let logic: ConditionalLogic = null;
  if (typeof conditionalLogic === 'string') {
    try {
      logic = JSON.parse(conditionalLogic) as ConditionalLogic;
    } catch (err) {
      options?.onParseError?.(err, conditionalLogic);
      return true;
    }
  } else {
    logic = conditionalLogic;
  }
  if (!logic || logic.enabled === false) return true;
  const rules = logic.rules ?? [];
  if (rules.length === 0) return true;
  const matcher = logic.matchMode === 'any' ? rules.some.bind(rules) : rules.every.bind(rules);
  return matcher((rule) => evalRule(rule, answersByQuestionNumber[rule.questionIndex]));
};

// gloss
export const buildAnswersByQuestionNumber = <T>(
  surveyAnswers: Record<string | number, T> | null | undefined,
  getAnswer: (v: T) => unknown = (v) => v,
): Record<number, unknown> =>
  Object.fromEntries(
    Object.entries(surveyAnswers ?? {}).map(([i, v]) => [Number(i) + 1, getAnswer(v)]),
  );
